/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
/**
 * VotersView — Milestone 2 voter list with filter/sort UI.
 *
 * Filter/sort spec: memory-bank/plans/filter-sort-ui-plan.md §5.1
 * API contract: memory-bank/plans/filter-sort-plan.md §5.1
 *
 * Layout:
 *   [H1: Voters] [Export CSV ⌄] [Saved views ⌄]
 *   [FilterBar: search, precinct, ld, tier, party, score, more filters]
 *   [Active filter chips + Clear all]
 *   [DataTable: checkbox | Name | Address | Precinct | Tier | Score | Last voted | Updated]
 *   [ResultsCount + pagination]
 *
 * URL-synced, server-side paginated (50 per page).
 */
import React, { useEffect, useState } from 'react';
import type { Voter, EngagementTier, PartySignal } from '@/types';
import { useListQuery } from '@/hooks/useListQuery';
import DataTable from '@/components/DataTable';
import {
  FilterBar,
  SearchInput,
  FilterSelect,
  FilterMultiSelect,
  FilterRange,
  FilterToggle,
} from '@/components/filters';
import client from '@/api/client';
import VoterMapPanel from '@/components/views/VoterMapPanel';
import VoterDetailDrawer from '@/components/VoterDetailDrawer';
import VoterStatsStrip from '@/components/VoterStatsStrip';

interface PrecinctOption {
  value: string;
  label: string;
}

interface LDOption {
  value: string;
  label: string;
}

/**
 * VotersView — server-paginated voter list with advanced filtering.
 */
export function VotersView({ onBack, userRole, onJumpToRoute }: { onBack?: () => void; userRole?: string; onJumpToRoute?: (subregion: string, shiftId: string, stopIndex: number) => void }) {
  // CANVASSER is in PII_READ_ROLES — the row-filtered `/voters` list below
  // works — but NOT in ANALYTICS_READ_ROLES, so /voters/stats and the
  // /voters/by-precinct + /voters/by-legislative-district dropdown feeds all
  // 403 for them. Rather than dead-end on those, drop the analytics stats
  // strip and the analytics-backed precinct/LD dropdowns for canvassers; the
  // route-filtered list (with search, tier, party, score filters) still works.
  const isCanvasser = (userRole ?? '').toLowerCase() === 'canvasser';

  // ============================================================================
  // State for dropdowns (load from backend)
  // ============================================================================
  const [precinctOptions, setPrecinctOptions] = useState<PrecinctOption[]>([]);
  const [ldOptions, setLDOptions] = useState<LDOption[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'map'>('list');
  const [selectedVoterId, setSelectedVoterId] = useState<number | null>(null);

  // ============================================================================
  // Query hook: server-side list query with envelope=v2
  // ============================================================================
  const query = useListQuery<Voter>({
    endpoint: '/voters',
    initialParams: { envelope: 'v2' },
    pageSize: 50,
  });

  // ============================================================================
  // Load dropdown options on mount
  // ============================================================================
  useEffect(() => {
    async function loadOptions() {
      // Canvassers can't read the analytics-gated precinct/LD feeds (403).
      // Skip the fetch entirely so we don't spam the console with 403s; the
      // precinct/LD dropdowns are hidden for them below anyway.
      if (isCanvasser) {
        setLoadingOptions(false);
        return;
      }
      try {
        setLoadingOptions(true);
        const [precinctRes, ldRes] = await Promise.all([
          client.get('/voters/by-precinct', { params: { page_size: 500 } }),
          client.get('/voters/by-legislative-district'),
        ]);

        // These endpoints return bare arrays, not list envelopes.
        const precinctRows = Array.isArray(precinctRes.data)
          ? precinctRes.data
          : (precinctRes.data?.items ?? []);
        const ldRows = Array.isArray(ldRes.data)
          ? ldRes.data
          : (ldRes.data?.items ?? []);

        const precincts: PrecinctOption[] = precinctRows.map((p: any) => ({
          value: String(p.precinct_num),
          label: `P${p.precinct_num} — ${p.precinct_name || ''}`.trim(),
        }));
        setPrecinctOptions(precincts);

        // by-legislative-district returns { district_key, district_raw }.
        const lds: LDOption[] = ldRows.map((ld: any) => ({
          value: String(ld.district_key ?? ld.legislative_district),
          label: `LD ${ld.district_key ?? ld.legislative_district}`,
        }));
        setLDOptions(lds);
      } catch (err) {
        console.error('Failed to load precinct/LD options:', err);
        // Gracefully degrade: empty dropdowns
      } finally {
        setLoadingOptions(false);
      }
    }

    loadOptions();
  }, []);

  // ============================================================================
  // Handle filter changes
  // ============================================================================
  const handleSearchChange = (value: string) => {
    query.setSearch(value);
  };

  const handlePrecinctChange = (value: string | null) => {
    if (value) {
      query.setFilter('precinct_num', Number(value));
    } else {
      query.setFilter('precinct_num', null);
    }
  };

  const handleLDChange = (value: string | null) => {
    if (value) {
      query.setFilter('legislative_district', value);
    } else {
      query.setFilter('legislative_district', null);
    }
  };

  const handleTierChange = (values: string[]) => {
    // Backend expects a single `tier` param, not `engagement_tier__in`.
    query.setFilter('tier', values.length > 0 ? values[0] : null);
  };

  const handlePartyChange = (value: string | null) => {
    query.setFilter('party', value);
  };

  const handleScoreChange = (value: [number | null, number | null]) => {
    // Backend expects `min_score` / `max_score`, not `score__gte` / `score__lte`.
    const [min, max] = value;
    query.setFilter('min_score', min);
    query.setFilter('max_score', max);
  };

  const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    query.setFilter('city', e.target.value || null);
  };

  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    query.setFilter('zip_code', e.target.value || null);
  };

  const handleStreetChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    query.setFilter('street_name', e.target.value || null);
  };

  const handleStatusChange = (value: string | null) => {
    query.setFilter('status', value);
  };

  const handlePrimaryVoterChange = (checked: boolean) => {
    // Backend declares primary_voter as int (0|1), not bool.
    query.setFilter('primary_voter', checked ? 1 : null);
  };

  // ============================================================================
  // Extract current filter values for controls and chips
  // ============================================================================
  const currentFilters = query.filters as Record<string, any>;
  const currentPrecinct = currentFilters.precinct_num
    ? String(currentFilters.precinct_num)
    : null;
  const currentLD = currentFilters.legislative_district || null;
  const currentTier = Array.isArray(currentFilters.tier)
    ? currentFilters.tier
    : currentFilters.tier
      ? [currentFilters.tier]
      : [];
  const currentParty = currentFilters.party || null;
  const scoreMin = currentFilters.min_score ?? null;
  const scoreMax = currentFilters.max_score ?? null;

  // ============================================================================
  // Build active filter chips
  // ============================================================================
  const activeFilterChips: Array<{ id: string; label: string; value: string }> = [];

  if (query.search) {
    activeFilterChips.push({
      id: 'search',
      label: 'Search',
      value: query.search,
    });
  }

  if (currentPrecinct) {
    const precinctLabel =
      precinctOptions.find((p) => p.value === currentPrecinct)?.label || `P${currentPrecinct}`;
    activeFilterChips.push({
      id: 'precinct',
      label: 'Precinct',
      value: precinctLabel,
    });
  }

  if (currentLD) {
    activeFilterChips.push({
      id: 'ld',
      label: 'LD',
      value: currentLD,
    });
  }

  if (currentTier.length > 0) {
    activeFilterChips.push({
      id: 'tier',
      label: 'Tier',
      value: currentTier.join(', '),
    });
  }

  if (currentParty) {
    activeFilterChips.push({
      id: 'party',
      label: 'Party',
      value: currentParty,
    });
  }

  if (scoreMin !== null || scoreMax !== null) {
    const scoreLabel =
      scoreMin !== null && scoreMax !== null
        ? `${scoreMin}–${scoreMax}`
        : scoreMin !== null
          ? `≥${scoreMin}`
          : `≤${scoreMax}`;
    activeFilterChips.push({
      id: 'score',
      label: 'Score',
      value: scoreLabel,
    });
  }

  if (currentFilters.city) {
    activeFilterChips.push({
      id: 'city',
      label: 'City',
      value: currentFilters.city,
    });
  }

  if (currentFilters.zip_code) {
    activeFilterChips.push({
      id: 'zip',
      label: 'Zip',
      value: currentFilters.zip_code,
    });
  }

  if (currentFilters.street_name) {
    activeFilterChips.push({
      id: 'street',
      label: 'Street',
      value: currentFilters.street_name,
    });
  }

  if (currentFilters.status) {
    activeFilterChips.push({
      id: 'status',
      label: 'Status',
      value: currentFilters.status,
    });
  }

  if (currentFilters.primary_voter) {
    activeFilterChips.push({
      id: 'primary_voter',
      label: 'Primary voter',
      value: 'Yes',
    });
  }

  // ============================================================================
  // Export CSV
  // ============================================================================
  const handleExportCsv = async () => {
    try {
      // Build params from current filter state
      const params = new URLSearchParams();
      params.set('format', 'csv');
      if (query.search) params.set('q', query.search);
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          params.set(`${key}__in`, value.join(','));
        } else if (value !== null && value !== undefined && value !== '') {
          params.set(key, String(value));
        }
      });

      const response = await client.get('/voters/walk-list/export', {
        params: Object.fromEntries(params),
        responseType: 'blob',
      });

      // Trigger download
      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `voters_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export CSV:', err);
      alert('Could not export CSV. Please try again.');
    }
  };

  // ============================================================================
  // Table columns definition
  // ============================================================================
  const columns = [
    {
      key: 'checkbox',
      header: '☐',
      sortable: false,
      hideOnCard: true,
      render: (row: Voter) => (
        <input
          type="checkbox"
          className="accent-emerald-500 min-h-11 min-w-11 sm:min-h-0 sm:min-w-0"
          aria-label={`Select voter ${row.last_name}, ${row.first_name}`}
        />
      ),
    },
    {
      key: 'last_name',
      header: 'Name',
      sortable: true,
      render: (row: Voter) => (
        <div>
          <div className="font-medium text-fg-primary">
            {row.last_name}, {row.first_name}
          </div>
        </div>
      ),
    },
    {
      key: 'address',
      header: 'Address',
      sortable: false,
      render: (row: Voter) => (
        <div className="text-sm text-fg-secondary">{row.address}</div>
      ),
    },
    {
      key: 'precinct_num',
      header: 'Precinct',
      sortable: true,
      render: (row: Voter) =>
        row.precinct_num ? `P${row.precinct_num}` : '—',
    },
    {
      key: 'engagement_tier',
      header: 'Tier',
      sortable: true,
      render: (row: Voter) => (
        <TierBadge tier={row.engagement_tier} />
      ),
    },
    {
      key: 'score',
      header: 'Score',
      sortable: true,
      render: (row: Voter) => (
        <ScoreBadge score={row.score} />
      ),
    },
    {
      key: 'last_voted_date',
      header: 'Last voted',
      sortable: true,
      // Date-only value: anchor to local midnight so it doesn't render a day
      // early in negative-offset timezones.
      render: (row: Voter) =>
        row.last_voted_date
          ? new Date(`${row.last_voted_date}T00:00:00`).toLocaleDateString()
          : '—',
    },
    {
      key: 'updated_at',
      header: 'Updated',
      sortable: true,
      render: (row: Voter) =>
        row.updated_at
          ? new Date(row.updated_at).toLocaleDateString()
          : '—',
    },
  ];

  // ============================================================================
  // Render
  // ============================================================================
  return (
    <div className="space-y-6">
      {/* Filter-aware stats strip — hidden for canvassers, who can't read
          /voters/stats (ANALYTICS_READ_ROLES). */}
      {!isCanvasser && (
        <VoterStatsStrip
          filters={query.filters}
          search={query.search}
          countyTotal={78336}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-end gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-border-base bg-bg-raised p-0.5" role="tablist" aria-label="View mode">
            <button
              role="tab"
              aria-selected={viewMode === 'list'}
              onClick={() => setViewMode('list')}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${
                viewMode === 'list'
                  ? 'bg-status-success-bg text-white'
                  : 'text-fg-secondary hover:text-fg-primary hover:bg-bg-elevated'
              }`}
            >
              ☰ List
            </button>
            <button
              role="tab"
              aria-selected={viewMode === 'map'}
              onClick={() => setViewMode('map')}
              className={`px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${
                viewMode === 'map'
                  ? 'bg-status-success-bg text-white'
                  : 'text-fg-secondary hover:text-fg-primary hover:bg-bg-elevated'
              }`}
            >
              🗺 Map
            </button>
          </div>
          <button
            onClick={handleExportCsv}
            disabled={query.loading || query.items.length === 0}
            className="bg-status-success-bg hover:bg-status-success-bg/80 disabled:opacity-50 text-white px-4 py-2 rounded-lg text-sm font-bold whitespace-nowrap"
            title="Export current filtered results as CSV"
          >
            ⬇ Export CSV
          </button>
        </div>
      </div>

      {/* Main filter bar */}
      <FilterBar
        activeFilters={activeFilterChips}
        onClearAll={query.clearAll}
      >
        <SearchInput
          value={query.search}
          onChange={handleSearchChange}
          placeholder="Search name / address"
        />

        {/* Precinct + LD dropdowns feed from analytics-gated endpoints that
            403 for canvassers — omit them rather than show empty controls. */}
        {!isCanvasser && (
          <FilterSelect
            label="Precinct"
            value={currentPrecinct}
            options={precinctOptions}
            onChange={handlePrecinctChange}
            placeholder="All precincts"
          />
        )}

        {!isCanvasser && (
          <FilterSelect
            label="Legislative District"
            value={currentLD}
            options={ldOptions}
            onChange={handleLDChange}
            placeholder="All LDs"
          />
        )}

        <FilterMultiSelect
          label="Tier"
          values={currentTier}
          options={[
            { value: 'anchor', label: 'Anchor' },
            { value: 'hot', label: 'Hot' },
            { value: 'warm', label: 'Warm' },
            { value: 'cool', label: 'Cool' },
          ]}
          onChange={handleTierChange}
        />

        <FilterSelect
          label="Party"
          value={currentParty}
          options={[
            { value: 'R', label: 'Republican' },
            { value: 'D', label: 'Democrat' },
            { value: 'U', label: 'Unaffiliated' },
            { value: 'O', label: 'Other' },
          ]}
          onChange={handlePartyChange}
          placeholder="All parties"
        />

        <FilterRange
          label="Score"
          min={0}
          max={100}
          value={[scoreMin, scoreMax]}
          onChange={handleScoreChange}
        />

        {/* More filters disclosure */}
        <button
          onClick={() => setShowMoreFilters(!showMoreFilters)}
          className="text-sm text-status-success hover:text-status-success/80 underline font-semibold rounded px-2 py-1"
        >
          {showMoreFilters ? '− More filters' : '+ More filters'}
        </button>
      </FilterBar>

      {/* More filters section (collapsible) */}
      {showMoreFilters && (
        <div className="bg-bg-raised rounded-lg border border-border-base p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
            <div className="flex flex-col gap-1">
              <label className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">
                City
              </label>
              <input
                type="text"
                value={currentFilters.city || ''}
                onChange={handleCityChange}
                placeholder="City"
                className="bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">
                Zip Code
              </label>
              <input
                type="text"
                value={currentFilters.zip_code || ''}
                onChange={handleZipChange}
                placeholder="Zip"
                className="bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label className="text-[10px] uppercase tracking-wide text-fg-muted font-semibold">
                Street Name
              </label>
              <input
                type="text"
                value={currentFilters.street_name || ''}
                onChange={handleStreetChange}
                placeholder="Street"
                className="bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
              />
            </div>

            <FilterSelect
              label="Status"
              value={currentFilters.status || null}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
              onChange={handleStatusChange}
              placeholder="All"
            />

            <div className="flex items-end gap-2">
              <FilterToggle
                label="Primary voter"
                checked={currentFilters.primary_voter || false}
                onChange={handlePrimaryVoterChange}
              />
            </div>
          </div>
        </div>
      )}

      {/* Data table / Map */}
      {viewMode === 'list' ? (
        <DataTable<Voter>
          columns={columns}
          query={query}
          rowKey={(row) => row.id}
          emptyMessage="No voters in this campaign."
          emptyFilteredMessage="No voters match these filters."
          onRowClick={(row) => setSelectedVoterId(row.id)}
          mobileMode="cards"
        />
      ) : (
        <VoterMapPanel filters={query.filters} search={query.search} />
      )}

      {/* Voter detail drawer */}
      <VoterDetailDrawer
        voterId={selectedVoterId}
        onClose={() => setSelectedVoterId(null)}
        onJumpToRoute={onJumpToRoute}
      />
    </div>
  );
}

export default VotersView;

// ============================================================================
// Sub-components
// ============================================================================

function TierBadge({ tier }: { tier?: EngagementTier }) {
  if (!tier) return <span className="text-fg-muted">—</span>;

  const tierColors: Record<EngagementTier, { bg: string; text: string }> = {
    anchor: { bg: 'bg-tier-anchor/30', text: 'text-tier-anchor' },
    hot: { bg: 'bg-tier-hot/30', text: 'text-tier-hot' },
    warm: { bg: 'bg-tier-warm/30', text: 'text-tier-warm' },
    cool: { bg: 'bg-tier-cool/30', text: 'text-tier-cool' },
    super_voter: { bg: 'bg-status-success-bg/30', text: 'text-status-success' },
  };

  const { bg, text } = tierColors[tier] || { bg: 'bg-bg-elevated/50', text: 'text-fg-secondary' };

  return (
    <span
      className={`inline-block px-2 py-1 rounded text-[11px] font-semibold uppercase ${bg} ${text}`}
    >
      {tier.charAt(0).toUpperCase() + tier.slice(1)}
    </span>
  );
}

function ScoreBadge({ score }: { score: number }) {
  let bgColor = 'bg-bg-elevated/50';
  let textColor = 'text-fg-secondary';

  if (score >= 85) {
    bgColor = 'bg-status-danger-bg/30';
    textColor = 'text-status-danger';
  } else if (score >= 70) {
    bgColor = 'bg-status-warning-bg/30';
    textColor = 'text-status-warning';
  } else if (score >= 55) {
    bgColor = 'bg-status-warning-bg/30';
    textColor = 'text-status-warning';
  }

  return (
    <span className={`inline-block px-2 py-1 rounded text-[11px] font-semibold tabular-nums ${bgColor} ${textColor}`}>
      {score}
    </span>
  );
}
